import { useState, useRef, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';

interface TextCellEditorProps {
  value: unknown;
  ariaLabel?: string;
  onChange: (value: string) => void;
  onCommit: () => void;
  onCancel: () => void;
}

export function TextCellEditor({ value, ariaLabel, onChange, onCommit, onCancel }: TextCellEditorProps) {
  const [localValue, setLocalValue] = useState(value != null ? String(value) : '');
  const inputRef = useRef<HTMLInputElement>(null);
  const localValueRef = useRef(localValue);
  localValueRef.current = localValue;
  const committedRef = useRef(false);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    // Place cursor at end (don't select — allows type-to-start to append naturally)
    const len = input.value.length;
    input.setSelectionRange(len, len);
  }, []);

  const commit = useCallback(() => {
    if (committedRef.current) return;
    committedRef.current = true;
    onChange(localValueRef.current);
    onCommit();
  }, [onChange, onCommit]);

  return (
    <Input
      ref={inputRef}
      value={localValue}
      aria-label={ariaLabel}
      onChange={e => setLocalValue(e.target.value)}
      onKeyDown={e => {
        if (e.key === 'Enter') { commit(); }
        if (e.key === 'Tab' && !committedRef.current) {
          committedRef.current = true;
          onChange(localValueRef.current);
        }
        if (e.key === 'Escape') { onCancel(); }
      }}
      onBlur={commit}
      className="cell-editing-input text-xs"
    />
  );
}
